import Bugsnag from '@bugsnag/js'
import { Box, Icon, Skeleton, Typography } from '@mui/material'
import AdxSwitch from 'components/reusable/switch/AdxSwitch'
import React from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import {
  getConnectedModules,
  updateConnectedModules,
} from 'services/api/modules/payroll/payroll-claims'

// Modules that can bring approved data into payroll. Add future modules here.
const MODULES = [
  {
    key: 'claims',
    icon: 'receipt_long',
    title: 'Claims',
    description:
      'Allow approved claims to be included in payroll runs. Claims are paid in the payslip and are not taxed.',
  },
]

const ConnectedModules = () => {
  const queryClient = useQueryClient()

  const { data: modules, isLoading } = useQuery('connectedModules', async () => {
    const { data } = await getConnectedModules()
    return data
  })

  const { mutate: saveModules, isLoading: saving } = useMutation(updateConnectedModules, {
    onSuccess: () => {
      queryClient.invalidateQueries('connectedModules')
      queryClient.invalidateQueries(['retrieveEmployeeData'])
    },
    onError: (err) => {
      Bugsnag.notify(err)
      toast.error('Unable to update connected modules. Please try again.')
    },
  })

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold">
        Payroll integrations
      </Typography>
      <Typography color="light-gray-text.main" mb={2}>
        Bring approved data from other modules into payroll.
      </Typography>
      <Box
        sx={{
          bgcolor: 'white.main',
          border: 1,
          borderColor: 'gray-line.main',
          borderRadius: '4.5px',
        }}
      >
        {MODULES.map((module, index) => (
          <Box
            key={module.key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              p: 2,
              borderTop: index === 0 ? 0 : 1,
              borderColor: 'gray-line.main',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'alternate.light',
                  color: 'alternate.main',
                }}
              >
                <Icon>{module.icon}</Icon>
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {module.title}
                </Typography>
                <Typography variant="body2" color="light-gray-text.main">
                  {module.description}
                </Typography>
              </Box>
            </Box>
            {isLoading ? (
              <Skeleton width={70} />
            ) : (
              <AdxSwitch
                state={!!modules?.[module.key]}
                disabled={saving}
                onChange={(e) => saveModules({ [module.key]: e.target.checked })}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default ConnectedModules
